/**
 * Market Selector registry.
 *
 * `config/markets.js` stays the single source of truth for what a market *is*
 * (name, symbol, leverage, description, icon). This file only adds how a market
 * should *look* in the selector's 3D coverflow: its accent colour and, for the
 * shape-specific objects, the handful of parameters that object needs.
 *
 * Mirrors the pattern of `Cockpit/cockpitMarkets.js`, but for the five
 * non-collectible categories. Nothing here touches the Pokédex cockpit.
 */

import { MARKETS, MARKET_CATEGORIES, getMarketsByCategory } from "../../config/markets";

/** Tabs across the top of the selector, in display order. */
export const SELECTOR_CATEGORIES = [
  { key: "all", label: "All" },
  { key: "crypto", label: "Crypto" },
  { key: "stocks", label: "Stocks" },
  { key: "commodities", label: "Commodities" },
  { key: "realestate", label: "Real Estate" },
  { key: "watches", label: "Watches" },
  { key: "collectibles", label: "Pokémon" },
];

/** Accent + copy per category. `#5B1DEE` is the app's brand purple. */
export const THEMES = {
  all: { accent: "#5B1DEE", cat: "All markets", head: "Every market on Coded Estate" },
  crypto: { accent: "#5B1DEE", cat: "Crypto", head: "Crypto perpetuals" },
  stocks: { accent: "#0E7C66", cat: "Stocks", head: "US equities & ETFs" },
  commodities: { accent: "#A8761A", cat: "Commodities", head: "Metals & commodities" },
  realestate: { accent: "#C0562F", cat: "Real Estate", head: "US housing markets" },
  // Lifted off the original navy: at #1F3A5F the accent disappeared into a dark
  // page. The watch *objects* keep their own metal colours — this is chrome.
  watches: { accent: "#3D7BD4", cat: "Watches", head: "Luxury watch indexes" },
};

/** Which 3D object each category renders. */
export const CATEGORY_SHAPE = {
  [MARKET_CATEGORIES.CRYPTO]: "coin",
  /* A share certificate, not a nameplate: a plaque represented nothing and sat
     in the same metal family as the bullion bar (ADDENDUM-7 §2). */
  [MARKET_CATEGORIES.STOCKS]: "cert",
  [MARKET_CATEGORIES.COMMODITIES]: "bar",
  [MARKET_CATEGORIES.REAL_ESTATE]: "tower",
  [MARKET_CATEGORIES.WATCHES]: "watch",
};

/** Category keys the selector handles — Collectibles is a passthrough. */
export const SELECTOR_CATEGORY_KEYS = [
  MARKET_CATEGORIES.CRYPTO,
  MARKET_CATEGORIES.STOCKS,
  MARKET_CATEGORIES.COMMODITIES,
  MARKET_CATEGORIES.REAL_ESTATE,
  MARKET_CATEGORIES.WATCHES,
];

/** Brand hues for the coin discs. Ids match `config/markets.js`. */
const CRYPTO_ACCENTS = {
  btc: "#F7931A",
  eth: "#6B7FD7",
  sol: "#14B98A",
  xrp: "#4A5560",
  bnb: "#F0B90B",
  doge: "#C2A633",
  avax: "#E84142",
  link: "#2A5ADA",
  ltc: "#8B96A5",
  near: "#3D4450",
  trump: "#C8A94B",
  hype: "#5BC7B4",
  sui: "#4DA2FF",
  ena: "#5C5C5C",
  arb: "#28A0F0",
  trx: "#EB0029",
  apt: "#3A3A3A",
  pol: "#8247E5",
  ton: "#0098EA",
  ada: "#0D1E30",
  aster: "#C2A15B",
  pump: "#2FBF6E",
  aave: "#B6509E",
  pengu: "#7FC3E8",
  bonk: "#F2A73B",
  shib: "#E8522F",
  kaito: "#3E5CD9",
  mnt: "#3E4145",
  ip: "#4A6CF7",
  virtual: "#2E6BFF",
  zec: "#ECB244",
  nibi: "#14C9C9",
};

/** Brushed-metal hues for the ticker plates. */
const STOCK_ACCENTS = {
  qqq: "#1F6FB2",
  spy: "#1B4F9C",
  nvidia: "#5E9400",
  amd: "#C41B22",
  intel: "#0068B5",
  microsoft: "#2C7BD4",
  apple: "#6E7378",
  alphabet: "#2E7CF6",
  amazon: "#E39A2A",
  "meta-stock": "#3B6FE0",
  broadcom: "#B01C22",
  tesla: "#CC2229",
  berkshire: "#2E4A7A",
  "coinbase-stock": "#1955D1",
  jpmorgan: "#4B6E9C",
  palantir: "#3E4A56",
  dwave: "#2E7F8F",
  quantum: "#5B49C4",
  gme: "#B5312F",
};

/**
 * Commodity looks. Metals are bullion bars stamped with a purity line; the
 * energy markets get their own vessels, and their sub-line carries the
 * delivery point instead — that is what tells WTI from Brent at a glance.
 * `band` colours the drum placard / cylinder band.
 */
const COMMODITY_STYLE = {
  "xau-usd": { a: "#D4AF37", purity: "SPOT · 999.9" },
  "xag-usd": { a: "#B9C0C7", purity: "SPOT · 999" },
  "xpt-usd": { a: "#9AA7B3", purity: "SPOT · 999.5" },
  "xpd-usd": { a: "#B8AEB6", purity: "SPOT · 999.5" },
  "wti-usd": { a: "#1C1C1E", purity: "CUSHING, OK", obj: "barrel", band: "#E8C33A" },
  "xbr-usd": { a: "#191A1C", purity: "NORTH SEA", obj: "barrel", band: "#D2552F" },
  "ng-usd": { a: "#D8DEE4", purity: "HENRY HUB, LA", obj: "gas", band: "#2F7CC4" },
};

/** Case metal and dial colour per watch brand. */
const WATCH_STYLE = {
  "watch-index": { a: "#8E9AA6", dial: "#1F3A5F" },
  rolex: { a: "#C9A227", dial: "#0F5132" },
  "patek-philippe": { a: "#B8B2A0", dial: "#243B53" },
  "audemars-piguet": { a: "#9BA6B2", dial: "#1B2733" },
  omega: { a: "#A3ADB8", dial: "#14161A" },
  cartier: { a: "#C6A15B", dial: "#F4EFE3" },
  breitling: { a: "#8C97A3", dial: "#1D2B3A" },
  tudor: { a: "#94A0AC", dial: "#7A1420" },
};

/**
 * Per-city architecture for the real-estate buildings.
 *
 * `tall` / `wide` are 0–1 proportion factors; `crown`, `win` and `back` select
 * the roofline, window pattern and skyline backdrop drawn in Tower3D.
 */
const CITY_STYLE = {
  "new-york":    { short: "NYC", tag: "Art Deco",      a: "#5C6B7A", trim: "#C9A227", tall: 0.94, wide: 0.46, crown: "spire",   win: "grid",   sky: "#DDE3EA", back: "towers" },
  brooklyn:      { short: "BRK", tag: "Brownstone",    a: "#9C5637", trim: "#E7DCCB", tall: 0.55, wide: 0.68, crown: "cornice", win: "arched", sky: "#F1E4D6", back: "rowhouses" },
  "los-angeles": { short: "LA",  tag: "Stucco",        a: "#E0C9A6", trim: "#C0562F", tall: 0.42, wide: 0.78, crown: "flat",    win: "ribbon", sky: "#FBE3C4", back: "sun" },
  // Dusk on Ocean Drive — coral facade, turquoise trim, amber sky.
  "miami-beach": { short: "MB",  tag: "Ocean Drive",   a: "#FF7E6B", trim: "#17C8D4", tall: 0.50, wide: 0.72, crown: "stepped", win: "ribbon", sky: "#FFC98F", back: "sunset" },
  boston:        { short: "BOS", tag: "Red Brick",     a: "#8E3B2F", trim: "#F3EDE3", tall: 0.58, wide: 0.62, crown: "pitched", win: "grid",   sky: "#E4E8EC", back: "rowhouses" },
  chicago:       { short: "CHI", tag: "Steel & Glass", a: "#3E4A56", trim: "#93A3B3", tall: 0.90, wide: 0.50, crown: "antenna", win: "grid",   sky: "#D6DEE6", back: "towers" },
  washington:    { short: "DC",  tag: "Limestone",     a: "#D8D2C4", trim: "#B4A98F", tall: 0.44, wide: 0.80, crown: "dome",    win: "arched", sky: "#E9EDE6", back: "none" },
  austin:        { short: "ATX", tag: "Glass Tower",   a: "#4E7C8C", trim: "#B9E2E8", tall: 0.82, wide: 0.50, crown: "angled",  win: "ribbon", sky: "#DCEDEF", back: "towers" },
  denver:        { short: "DEN", tag: "Front Range",   a: "#7B7F8B", trim: "#CBD3DC", tall: 0.62, wide: 0.56, crown: "flat",    win: "grid",   sky: "#DCE6F0", back: "mountains" },
  pittsburgh:    { short: "PIT", tag: "Steel City",    a: "#4F6355", trim: "#D8C9A3", tall: 0.66, wide: 0.54, crown: "pitched", win: "grid",   sky: "#DFE3DD", back: "bridge" },
};

/** Markets whose ticker plate reads ETF rather than an exchange name. */
const ETF_IDS = new Set(["qqq", "spy"]);

/** Fallback city look for any real-estate market added after this registry. */
const CITY_FALLBACK = { tag: "Metro", a: "#6B7684", trim: "#C7CED6", tall: 0.7, wide: 0.58, crown: "flat", win: "grid", sky: "#DEE4EA", back: "towers" };

/**
 * Deterministic accent for a market with no registered brand colour, so a newly
 * added market still renders a sensible object instead of falling back to grey.
 * @param {string} seed - Market id
 * @returns {string} "#rrggbb"
 */
const hashedAccent = (seed) => {
  let h = 0;
  for (let i = 0; i < String(seed).length; i++) h = (h * 31 + String(seed).charCodeAt(i)) >>> 0;
  const hue = h % 360;
  const sat = 0.42;
  const light = 0.46;
  const c = (1 - Math.abs(2 * light - 1)) * sat;
  const x = c * (1 - Math.abs(((hue / 60) % 2) - 1));
  const m = light - c / 2;
  const [r, g, b] =
    hue < 60 ? [c, x, 0] : hue < 120 ? [x, c, 0] : hue < 180 ? [0, c, x] :
    hue < 240 ? [0, x, c] : hue < 300 ? [x, 0, c] : [c, 0, x];
  const hex = (v) => Math.round((v + m) * 255).toString(16).padStart(2, "0");
  return `#${hex(r)}${hex(g)}${hex(b)}`;
};

/**
 * `rgba()` from a hex colour. Companion to `shade` in `Cockpit/fx.js`.
 * @param {string} hex - "#rgb" or "#rrggbb"
 * @param {number} a - 0..1
 * @returns {string}
 */
export const alpha = (hex, a) => {
  let c = String(hex || "#888888").replace("#", "");
  if (c.length === 3) c = c.split("").map((x) => x + x).join("");
  const r = parseInt(c.slice(0, 2), 16);
  const g = parseInt(c.slice(2, 4), 16);
  const b = parseInt(c.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${a})`;
};

/** Venue line under a stock's ticker — "ETF", or the exchange it trades on. */
const venueFor = (market) => {
  if (ETF_IDS.has(market.id)) return "ETF";
  const tv = market.tradingViewSymbol || "";
  const exchange = tv.includes(":") ? tv.split(":")[0] : "";
  if (exchange === "AMEX") return "NYSE AMERICAN";
  return exchange || "EQUITY";
};

/**
 * Decorate a market from `config/markets.js` with everything the coverflow
 * objects need. Unknown ids still get a usable object via the fallbacks.
 *
 * @param {object} market - A `MARKETS` entry
 * @returns {object} The market plus `shape`, `accent`, `short` and shape params
 */
export const describeMarket = (market) => {
  const shape = CATEGORY_SHAPE[market.category] || "coin";
  const base = { ...market, shape, short: market.symbol, cat: market.category };

  if (shape === "coin") {
    return { ...base, accent: CRYPTO_ACCENTS[market.id] || hashedAccent(market.id) };
  }
  if (shape === "cert") {
    return { ...base, accent: STOCK_ACCENTS[market.id] || hashedAccent(market.id), sub: venueFor(market) };
  }
  if (shape === "bar") {
    const s = COMMODITY_STYLE[market.id];
    // Object choice is no longer purely by category — crude oil in a bullion
    // bar was wrong, so energy markets override the shape.
    return {
      ...base,
      shape: s?.obj || shape,
      accent: s?.a || hashedAccent(market.id),
      band: s?.band,
      sub: s?.purity || "SPOT",
    };
  }
  if (shape === "watch") {
    const s = WATCH_STYLE[market.id];
    return { ...base, accent: s?.a || "#8E9AA6", dial: s?.dial || "#1B2733" };
  }

  // tower — the per-city building
  const c = CITY_STYLE[market.id] || { ...CITY_FALLBACK, short: market.symbol.slice(0, 3) };
  return { ...base, accent: c.a, city: c, short: c.short };
};

/**
 * Every market the selector can show, in category order, already decorated.
 *
 * Collectibles (RWA) and forex are excluded: RWA keeps its own Pokédex
 * switcher, and forex has no object design.
 *
 * @param {string} categoryKey - `all` or one of the `MARKET_CATEGORIES` values
 * @returns {Array<object>} Decorated markets
 */
export const getSelectorMarkets = (categoryKey) => {
  const source =
    categoryKey && categoryKey !== "all"
      ? getMarketsByCategory(categoryKey)
      : SELECTOR_CATEGORY_KEYS.flatMap((k) => getMarketsByCategory(k));
  return source.filter((m) => SELECTOR_CATEGORY_KEYS.includes(m.category)).map(describeMarket);
};

/** Total markets the selector covers — used only by tests and the counter. */
export const SELECTOR_MARKET_COUNT = MARKETS.filter((m) => SELECTOR_CATEGORY_KEYS.includes(m.category)).length;
